/**
 * GameScreen.tsx —— 牌桌页整体布局（设计方案 4.2 信息架构）
 *
 * 布局职责在这一层，业务时序全部委托给 GameController；
 * 组件只订阅 version / equity / settings 三个 store。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import type { Action } from '../core/betting';
import type { GameController, SetupParams } from '../state/controller';
import { patchSettings, type Settings } from '../state/settings';
import { chips, STREET_CN, signedChips } from '../state/format';
import { track } from '../state/telemetry';
import { setLetwayFloat } from '../state/letway';
import { useBreakpoint, useEquityPanel, useGameVersion, usePortraitTouch, useShortViewport, useTicker, useToasts } from './hooks';
import { Drawer, ToastLayer } from './primitives';
import { overlayOpen } from './overlay';
import {
  pickSizerMode,
  placeOverlay,
  sizerContentMin,
  SIZER_NEED,
  type OverlayPlacement,
  type SizerMode,
} from './overlayPlacement';
import { scoreTipVisible } from './scoreTip';
import { solveFloatTop, solveFloatDetailMaxH } from './overlayReservation';
import { ScoreTip } from './ScoreTip';
import { Table } from './Table';
import { ActionBar } from './ActionBar';
import { EquityPanel } from './EquityPanel';
import { HistoryDrawer } from './HistoryDrawer';
import { SettingsPanel } from './SettingsPanel';
import { EliminatedDialog, GameOverScreen } from './EndScreens';

const HUMAN_SEAT = 0;

export function GameScreen({
  c,
  settings,
  lastSetup,
  onHome,
  onRestart,
}: {
  c: GameController;
  settings: Settings;
  lastSetup: SetupParams | null;
  onHome: () => void;
  onRestart: (p: SetupParams) => void;
}) {
  useGameVersion(c);
  useTicker(250); // 结算倒数 / 时限进度等纯展示型时间
  const eq = useEquityPanel(c);
  const bp = useBreakpoint();
  /**
   * FR-B（AC-B.2）：toast 从 App 根迁入 GameScreen 的 overlay 根（见文末 .gs__overlay）。
   * 牌桌在场时由本组件在 overlay 内渲染 ToastLayer（z:115，低于结算 140）；
   * StartScreen / 多人不在此组件树，仍由 App 根挂载。
   */
  const toasts = useToasts();
  /**
   * REG2-NEW-002：极矮视口（≤430px 高，典型是手机横屏 640×360 / 844×390）。
   * 宽度断点管不到「高度不够」这件事，因此单独一条，用来同时决定：
   *   ① 席位布局强制走 stack（oval 的定高毛毡在 390px 高的视口里会顶出可视区）；
   *   ② 席位统一压到紧凑档；
   *   ③ 挂 `gs--short` 类，交给样式层收紧顶栏 / 页脚等固定占用（见 layout.css 极矮视口段）。
   */
  const shortVp = useShortViewport();
  /**
   * 竖屏触屏（手机竖握）强制走 stack 三段式布局。见 usePortraitTouch 注释：
   * 高分屏安卓机竖屏上报的 CSS 宽度可能落进 md 而误走椭圆牌桌，导致公共牌带被椭圆
   * 曲边裁掉（首张公共牌点数缺失）。stack 布局下公共牌独占顶行、全宽不裁切，各人数稳定。
   */
  const portraitTouch = usePortraitTouch();
  /** 统一的「是否走 stack 布局」判据：窄断点 / 极矮视口 / 竖屏触屏，任一命中即 stack。 */
  const stackLayout = bp === 'sm' || shortVp || portraitTouch;
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * R3（第 10 轮）：评分提示的**显式关闭**标记。存的是被关闭那条评分的 `at`（唯一标识）；
   * 新评分（`at` 变化）不会被旧的 `dismissedAt` 命中，因此关闭只对当前这条生效（`scoreTipVisible`）。
   * 「常驻到下一手操作」的关闭由 controller 事件（提交下一动作 / 进入新一手需重新行动）负责，
   * 那种情况下 `c.lastScore` 直接变 null，天然不可见。
   */
  const [dismissedScoreAt, setDismissedScoreAt] = useState<number | null>(null);
  /**
   * R1（第 9 轮）：结算浮层退出淡出。
   * `settleClosing` 期间给浮层挂 `.settle--closing`（opacity → 0，140ms），淡出后再真正续手，
   * 避免「浮层瞬间消失 + 新一手瞬间铺开」的生硬跳变。`advancingRef` 防连点 / 防重复推进。
   */
  const [settleClosing, setSettleClosing] = useState(false);
  /**
   * Round21-M1 · T3 / AC-C1~C4：结算浮层「收起（Peek）↔ 展开」三态的收起标记。
   * `false` = Expanded（实底浮层 + scrim + 焦点陷阱，推进走 CTA）；
   * `true`  = Peek（浮层收成顶部瘦条 + 召回钮，撤 scrim/inert）→ 英雄手牌 / hero.cat / 5 张公共牌
   *   全部 `visFrac=1`（AC-C2/C4），且结算内容 **保持挂载**（恢复后与收起前 diff=0）；收起 ≠ 推进。
   * 每手进入 handComplete 时重置为 Expanded（见下方 effect）。
   */
  const [settleCollapsed, setSettleCollapsed] = useState(false);
  /**
   * Round22 · F3（AC3.1~AC3.4）：淘汰模态 / 终局大屏的「收起（Peek）↔ 展开」标记，复用 Round21
   * DismissContract 语义。`false` = Expanded（淘汰=阻断模态；终局=铺满 scrim 的全屏浮层）；
   * `true` = Collapsed（收成顶部瘦条、撤遮罩、露出**保持挂载**的牌桌可复盘他人底牌）。收起 ≠ 退出，
   * 顶部瘦条整条可点召回。进入淘汰 / 终局时各自重置为 Expanded（见下方 effect）。
   */
  const [eliminatedCollapsed, setEliminatedCollapsed] = useState(false);
  const [gameOverCollapsed, setGameOverCollapsed] = useState(false);
  const advancingRef = useRef(false);
  const beginAdvance = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setSettleClosing(true);
    window.setTimeout(() => {
      c.nextHandNow();
      setSettleClosing(false);
      advancingRef.current = false;
    }, 140);
  }, [c]);
  const openScoreReplay = useCallback(() => {
    track('replay_open', { source: 'score_tip', hands: c.handHistory().length });
    setHistoryOpen(true);
  }, [c]);
  // 加注抽屉是否展开：窄断点下赢率浮层要为它让位，避免两层浮层互相叠压（图层规则）
  const [sizerOpen, setSizerOpen] = useState(false);
  /**
   * QA-BUG-005：页脚（行动区）实测高度写进 `--foot-h`，供设置 / 历史抽屉的遮罩让位使用。
   * 抽屉遮罩改前是全屏 `inset:0`，把行动按钮的点击全吃掉（命中率 0/N）；
   * 让位需要一个真实高度，而页脚高度受断点、赢率横条、评分提示槽影响，只能实测。
   * 这里只做几何测量，不参与任何业务时序。
   */
  const [footEl, setFootEl] = useState<HTMLElement | null>(null);
  const [footH, setFootH] = useState(0);
  useEffect(() => {
    if (!footEl) {
      setFootH(0);
      return;
    }
    const measure = (): void => setFootH(footEl.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(footEl);
    return () => ro.disconnect();
  }, [footEl]);

  const game = c.game;
  // 席位名字表：按内容而非引用记忆。改前这里每次渲染都新建数组，
  // 会让 HistoryDrawer 的 useMemo 必然失效并把复盘游标打回末尾（NEW-1 / AC5.4）。
  const nameKey = game ? game.players.map((p) => p?.name ?? '').join('\u0001') : '';
  const names = useMemo(() => (nameKey ? nameKey.split('\u0001') : []), [nameKey]);

  /*
   * H 打开历史 / S 打开设置。
   *
   * QA-BUG-001 的同一条闸门：二次确认气泡挂着时，任何快捷键都不该越过它抢走焦点与状态
   * （气泡是「已经按下按钮、只差再点一下」的关键决策窗口）。浮层自己的关闭出口是 Esc
   * 与遮罩点击，由 useDismissable 负责，因此这里直接让位即可。
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (overlayOpen()) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key.toLowerCase() === 'h') {
        setHistoryOpen((v) => {
          if (!v) track('replay_open', { source: 'shortcut', hands: c.handHistory().length });
          return !v;
        });
      } else if (e.key.toLowerCase() === 's') {
        setSettingsOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [c]);

  /**
   * R1（第 9 轮）：自动继续到点推进。
   * 控制器只为「在座玩家 + 开启自动继续 + 非摊牌手」设 `nextHandAt`（≥5s 截止点，不除以倍速）；
   * 到点由这里带淡出推进（beginAdvance）。摊牌手 / 关闭自动继续时 `nextHandAt` 为 null，纯手动。
   * 观战态（humanEliminated）由控制器自行续手，这里不介入，避免双重推进。
   */
  useEffect(() => {
    if (!game || game.phase !== 'handComplete') return;
    if (c.humanEliminated) return;
    if (c.nextHandAt === null) return;
    const delay = Math.max(0, c.nextHandAt - Date.now());
    const t = window.setTimeout(() => beginAdvance(), delay);
    return () => window.clearTimeout(t);
  }, [c, game, game?.phase, c.nextHandAt, c.humanEliminated, beginAdvance]);

  /**
   * R1（第 9 轮）：结算浮层默认手动推进 —— Enter / 空格进入下一手（保留按钮供 QA 脚本驱动）。
   * 与 H/S 快捷键同一条闸门：有浮层（设置 / 历史 / 二次确认）时让位；输入控件聚焦时不抢键。
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!game || game.phase !== 'handComplete' || c.humanEliminated) return;
      if (overlayOpen()) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        beginAdvance();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [c, game, game?.phase, c.humanEliminated, beginAdvance]);

  /**
   * R3（第 10 轮）：Esc 关闭当前决策评分提示（DESIGN 4.2 生命周期表）。
   * 与其它快捷键同一条闸门：任何浮层（设置 / 历史 / 二次确认）打开时让位（它们的 Esc 归自己）。
   * 只写「显式关闭标记」，不触碰引擎状态；关闭后本手内该条不再重现（复盘仍可查）。
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (overlayOpen()) return;
      const at = c.lastScore?.at ?? null;
      if (at === null) return;
      setDismissedScoreAt(at);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [c]);

  /**
   * QA-BUG-012（展开态浮层避让）：md「赢率条」的展开详情浮层、sm/min360「底部抽屉」的展开态、
   * 以及窄屏「加注抽屉」，都是锚在页脚 / 按钮行上沿之上的绝对定位浮层。改前它们只按
   * `bottom: 100% + 8px` + 固定 max-height 放置，实测把「我」的席位卡与两张底牌整块吞掉：
   *   - 840×1100（md，赢率条展开）：浮层 240 高，底边 923，我的席位卡 556~726 被压 → 底牌可见比 0；
   *   - 390×844 / 360×640（sm/min360，赢率抽屉展开）：席位卡与底牌遮挡 100%（命中归属为 `segmented`）；
   *   - 390×844 / 360×640（加注抽屉）：抽屉 300 高盖住席位卡，重叠 5456px²、底牌可见比 0。
   * 「要看赢率 / 要选加注额，就看不见自己的牌」是决策阻断，必须避让。
   *
   * 避让不能写死数值：三条线（页脚上沿、按钮行上沿、我的席位卡位置）随断点、人数档、
   * 赢率条形态、提示槽有无而变。这里实测后折算成两组 CSS 变量交给样式层，规则统一为
   * **「优先放在我的席位卡下方的空白带；放不下才抬到席位卡上方，并让出公共牌 + 底池带」**：
   *
   *   放置带 A（席位卡下方）：宽屏 oval 布局下毛毡是定高居中的，毛毡下沿到页脚上沿之间有
   *     真实空白（md 实测 189px）。此时 lift 取最小值 8，限高 = 锚点上沿 - 席位卡下沿 - 16。
   *     改前 md 就是因为「位置对了但限高按 240 硬给」，才反过来压住席位卡。
   *   放置带 B（席位卡上方）：窄屏 stack 布局下我的席位卡紧贴页脚，A 带不足 140px（sm 实测仅 2px），
   *     改为把底边抬到席位卡上沿之上（lift = 锚点上沿 - 席位卡上沿 + 8），限高到公共牌带下沿
   *     （= 席位卡上沿 - 公共牌带下沿 - 16）。被覆盖的只剩「可滚动查看的对手区」。
   *
   * 赢率浮层锚在页脚上沿，加注抽屉锚在 `.actionbar__dock`（按钮行）上沿——后者更低（页脚里
   * 还有赢率条 / 提示槽 / 筹码胶囊），所以两者必须各算一份，不能共用同一个 lift。
   * 浮层是绝对定位、不参与页脚流高，因此不存在「测量 → 布局 → 再测量」的回环。
   */
  const MIN_OPEN_H = 140;
  const [eqBox, setEqBox] = useState<{
    lift: number;
    max: number;
    /** DEF-01：展开态 portal 到 overlay 根后，面板底边相对视口底的距离（= innerHeight - 底边Y）。 */
    openBottom: number;
    sizerLift: number;
    sizerMax: number;
    /** DEF-02：加注抽屉 portal 到 overlay 根后，抽屉底边相对视口底的距离（= innerHeight - 底边Y）。 */
    sizerOpenBottom: number;
    centerMaxTop: number;
    /** R21-M5 · BUG-01：stack 布局结算 Expanded 态浮层顶边（视口 Y）——落在受保护公共牌带下沿之下，
     *  不再 top:50% 居中盖住 board（AC-B6）。null/未测时 CSS 回退 50%。 */
    settleTop: number;
    /** R21-M5 · BUG-01：结算 Expanded 态可用高度（板带下沿 → 页脚上沿），超出即浮层内部滚动。 */
    settleMax: number;
    /** BLK-F1：抽屉承载模式（由实测硬带宽 + 视口高分级求解，见 overlayPlacement.pickSizerMode） */
    sizerMode: SizerMode;
    /** 诊断用：选档时的实测可用带宽，写进 DOM 供量测脚本取证 */
    sizerBand: number;
    /** Round26 · C4：ScoreTip float 顶边 = max(52, 顶栏下沿 + gap)，不压 PZ4 顶栏。 */
    scoretipTop: number;
    /** Round26 · C4：ScoreTip float 展开详解的最大高度（底边 ≤ bottomSafeBandTop − gap，超高内部滚动）。 */
    scoretipDetailMax: number;
    /** Round26 · C3（问题1）：竖屏窄带无纯几何解 → sizer 面板顶部镜像 Hero 手牌（方案 A）。 */
    heroMirror: boolean;
  } | null>(null);
  useEffect(() => {
    const measure = (): void => {
      const foot = footEl?.getBoundingClientRect();
      const meEl = document.querySelector('.felt__seat--me');
      const centerEl = document.querySelector('.felt__center');
      const feltEl = document.querySelector('.felt');
      if (!foot || !meEl) return;
      const me = meEl.getBoundingClientRect();
      const center = centerEl?.getBoundingClientRect();
      const centerBottom = center ? center.bottom : 0;
      const gap = 8;
      /*
       * Round26 · C2（问题1）：Hero 手牌上沿作为 sizer 的**独立硬界**。
       * 现状 sizer 只避让 `.actionbar` 顶沿（barTop），当 Hero 底牌浮出席位盒上沿（sm 竖屏
       * hero 尺寸牌 + 负外边距）时，上抬的 sizer 会盖住底牌（问题1「遮挡底牌」直接原因）。
       * 这里量 Hero 两张底牌的并集上沿 heroCardsTop，取不到牌面时回退席位盒上沿 me.top。
       * 下方把它并入 sizerLimitTop = min(barTop, heroCardsTop)：只会**收紧**下界（heroCardsTop
       * ≥ barTop 时为 no-op，逐像素等价 HEAD），绝不放宽，因此不会波及既有已通过的格。
       */
      const heroCardEls = Array.from(meEl.querySelectorAll('.pcard, .seat__card'));
      const heroCardsTop = heroCardEls.reduce<number | null>((acc, el) => {
        const r = el.getBoundingClientRect();
        if (r.width <= 0 && r.height <= 0) return acc;
        return acc == null ? r.top : Math.min(acc, r.top);
      }, null) ?? me.top;
      /**
       * 给定「浮层底边不得越过的下界线」，算出目标底边绝对 Y 与限高。
       *
       * 求解本身已抽到 `overlayPlacement.ts`（纯函数 + 单测锁定），这里只负责喂实测量。
       * 约束优先级见该文件文件头：受保护读数区（公共牌 + 底池）与我的席位卡 / 把手 / 按钮行
       * 都是**硬界**，浮层高度可让（内部滚动），视觉间距最后才让。
       * REG3-NEW-001（min360×2 人切底池 616px²）的修法就是把这层优先级显式化，
       * 不再用静态高度地板去压硬界。
       */
      const place = (limitTop: number, contentMin?: number, minOpenH: number = MIN_OPEN_H): OverlayPlacement =>
        placeOverlay({
          limitTop,
          meTop: me.top,
          meBottom: me.bottom,
          protectedBottom: centerBottom,
          gap,
          minOpenH,
          contentMin,
        });
      const eq = place(foot.top);
      /*
       * QA-BUG-018：加注抽屉盖掉整块赢率信息（sm/min360 覆盖比 1.0、面板与数值可见比 0）。
       * 两处根因，必须一起修，只改一处仍然会盖：
       *
       * 1) 下界线取错了。抽屉此前只避让 `.actionbar__dock`（按钮行）上沿，
       *    而折叠态赢率把手是**文档流**元素，就躺在页脚里、按钮行之上；于是几何上
       *    「没盖按钮」成立、「盖住把手」照旧。这里把把手上沿也纳入下界线取更小者。
       * 2) lift 的参照系错了。抽屉的包含块是「最近的已定位祖先」（实测为 `.actionbar__dock`，
       *    它带 `.pop-anchor` / position:relative），`bottom: calc(100% + lift)` 等价于
       *    「底边 = 该包含块 padding 盒上沿 - lift」，可此前 lift 是按 dock 上沿（未算包含块）
       *    甚至 `.actionbar` 上沿算的，两者差着「筹码信息行 + 内边距」≈47px。
       *    改成先算目标底边绝对 Y，再按实测包含块换算 lift，避让才是可复算的。
       */
      const dock = document.querySelector('.actionbar__dock');
      const barEl = document.querySelector('.actionbar');
      const shellEl = document.querySelector('.eqshell');
      const shell = shellEl?.getBoundingClientRect();
      const dockTop = dock ? dock.getBoundingClientRect().top : foot.top;
      /*
       * R11-1（W-1）：抽屉的下界线取 `.actionbar` 顶沿，而非按钮行 `.actionbar__dock` 顶沿。
       * `.actionbar__info`（B2 下注信息条）在 DOM 上位于 dock 之上、actionbar 之内，
       * 若只避让 dockTop，抽屉底边会落到 dockTop−8（仍在 actionbar 内），正好压住 B2。
       * 改取 actionbar 顶沿后 bottomY = barTop−8，B2 与四个行动按钮全程完整可见（命中 0/5）。
       */
      const barTop = barEl ? barEl.getBoundingClientRect().top : dockTop;
      // 只有「在文档流里、且位于按钮行上方」的把手才需要避让；
      // 展开态赢率浮层本身是绝对定位（bottom 已由 --eq-lift 抬走），不参与这条下界。
      /*
       * QA-BUG-R5-DRIFT：sizer 现在所有断点都是浮层，这条「把手让位」下界只对**窄断点
       * (md/sm) 页脚内的折叠把手**成立。lg 是绝对定位浮层、xl 赢率面板在 <aside> 里
       * （都不在页脚文档流），若把它们当把手会把 sizer 下界线推到面板上沿 → 宽屏 sizer
       * 被挤到视口顶部、压成 26px 不可用。故显式要求「窄断点 且 shell 在页脚元素内」。
       */
      const shellIsNarrowHandle =
        bp !== 'xl' && bp !== 'lg' && !!footEl && !!shellEl && footEl.contains(shellEl);
      const shellInFlow = shellIsNarrowHandle && !!shell && shell.height > 0 && shell.bottom <= dockTop + 1;
      /*
       * BLK-F1：抽屉这一路要带上「内容自然高度需求」。
       * 赢率浮层不传（它本来就是可滚动的长内容，没有「必须完整可见」的热区），
       * 抽屉传 `sizerContentMin(innerHeight)` —— 三件控件（快捷档 / 滑杆 / 确认 CTA）
       * 是指针路径的唯一入口，宁可让掉 8px 观感也不能让它们只露一半（568 高实测）。
       */
      /*
       * BLK-F1（方案 B）：抽屉这一路是**两遍求解**，顺序不能颠倒：
       *
       *   第 1 遍：不传 contentMin，只为拿到这条放置带在零视觉间距时的可用高度 `hardBand`。
       *            它只由「受保护读数区下沿 / 席位卡上沿 / 把手与按钮行上沿」决定，
       *            与抽屉内容无关 —— 这正是不会出现测量回环的原因。
       *   第 2 遍：按 `pickSizerMode(hardBand, 视口高)` 选出承载模式，再把该模式的纵向需求
       *            当作 contentMin 传回去求解，让求解器决定要不要为它让出 P3 视觉间距。
       *
       * 选出的模式同时写进 DOM（`data-sizer-mode`）：样式层据此换排版，量测脚本据此取证。
       * 这样「装不下就降一档交互」是显式的一步，而不是靠一轮一轮挤压参数去凑。
       */
      /*
       * R11-1（P-2）：portrait-stack「底部停靠优先」。
       * 抽屉落在「我的席位下沿 → 行动区顶沿」的空白带（bottomY = actionbar 顶 − gap），
       * 不再 `full` 浮在对手网格上压住 B4。该带里只有折叠态赢率**把手**（非受保护读数 B1–B4），
       * 抽屉可覆盖它 —— 因此 stack 直接以 actionbar 顶沿为下界（不被把手上抬），
       * 且给一个更小的 below-band 门槛（short 单行 66 即可停靠），让求解器优先选「席位卡下方」。
       * oval（wide）沿用 shellInFlow 避让 + MIN_OPEN_H，不受影响。
       */
      const layoutIsStack = stackLayout;
      /*
       * R14-2：短视口横屏（≤430 高，如 932×430 / 844×390）的「席位卡下方」空白带只有 ~59px
       * （我的席位卡在两列布局里仍占 ~124px 高，把 barTop − meBottom 压到 59），比 stack 的
       * 66 门槛略低，于是改前 placeOverlay 放弃底带、退到「公共牌带 → 席位卡上沿」的 8px 上带，
       * 抽屉被挤成 8px、且浮到牌桌顶部横贯遮挡对手气泡（B5）。这里把短视口横屏的底带门槛降到 52，
       * 让求解器仍选「席位卡下方」这条不遮挡任何保护项的带（drawer 落 t≈266–325，我方席位卡下沿
       * 258 之下、行动区上沿之上），配合横屏单行排版（layout.css 的 max-height:430 媒体查询）不滚动。
       * 竖屏（430×932，非短视口）与桌面 oval 不受影响，仍分别用 66 / MIN_OPEN_H。
       */
      const sizerMinOpenH = shortVp ? 52 : layoutIsStack ? 66 : MIN_OPEN_H;
      /*
       * Round26 · C2/C3（问题1）：Hero 手牌硬界 + 「窄带无解」兜底判定。
       * barLimit = 只避 ActionBar（原 HEAD 口径）；heroLimit = 再并 Hero 手牌上沿（收紧，不放宽）。
       * 先用 heroLimit 探测「既避按钮又避手牌」的可用带 heroProbe.hardBand：
       *   · ≥ compact(114)：几何有解 → 用 heroLimit（面板落在 Hero 手牌上沿之上，不遮底牌）。
       *   · < compact：竖屏窄带无纯几何解 → 启用方案 A（面板顶部镜像 Hero 手牌），此时允许面板
       *     覆盖真实 Hero 手牌区（镜像已保证「下注看得见牌」），下界回退 barLimit 拿回完整纵向预算。
       * heroCardsTop ≥ barTop 时 heroLimit == barLimit、heroMirror 恒 false，逐像素等价 HEAD。
       */
      const barLimit = layoutIsStack
        ? barTop
        : shellInFlow
          ? Math.min(barTop, shell.top)
          : barTop;
      const heroLimit = Math.min(barLimit, heroCardsTop);
      const heroProbe = place(heroLimit, undefined, sizerMinOpenH);
      const heroMirror = heroProbe.hardBand < SIZER_NEED.compact;
      const sizerLimitTop = heroMirror ? barLimit : heroLimit;
      const sizerProbe = place(sizerLimitTop, undefined, sizerMinOpenH);
      let sizerMode = pickSizerMode({
        hardBand: sizerProbe.hardBand,
        viewportH: window.innerHeight,
        /*
         * R14-1：移动端（stack 竖/横屏两目标 430×932 / 932×430）承载下限 = compact，
         * 不允许退回方案 B 的 `short`（无滑杆）。stack 布局即移动端口径：sm 断点（竖屏窄）
         * 或短视口（横屏矮）。桌面 md/lg 窄窗不置此位，仍可按声明式短屏阈值退到 short。
         */
        mobileFloor: layoutIsStack,
      });
      /*
       * R11-1（P-2）：stack 布局封顶到 `compact` —— `full`（240px）必然浮出席位卡下空白带、
       * 压住对手网格 B4；只允许 compact / short 停靠底带。wide-ring（oval）不封顶。
       */
      if (layoutIsStack && sizerMode === 'full') sizerMode = 'compact';
      const sizer = place(sizerLimitTop, sizerContentMin(sizerMode), sizerMinOpenH);
      /*
       * lift 的参照系必须是抽屉**真实的包含块**，否则避让会整体平移。
       * 抽屉 CSS 是 `bottom: calc(100% + var(--sizer-lift))`，绝对定位的 `bottom`
       * 相对「最近的已定位祖先的 padding 盒」结算，因此底边绝对 Y = 包含块 padding 盒上沿 - lift。
       *
       * 第一版按 `.actionbar` 换算，但按钮行 `.actionbar__dock` 自带 `.pop-anchor`
       * （position:relative，给全下确认气泡做锚点），它才是真包含块——两者相差
       * 「筹码信息行 + 内边距」≈47px，于是抽屉比预期低 47px：窄屏下正好压住我的底牌
       * （r02 实测 sizerOverHole sm:3128px² / min360:5704px²，底牌可见比 0.5 / 0）。
       * 这里改成实测「最近的已定位祖先」，谁定位就按谁换算，不再假定层级。
       */
      const anchorEl = ((): Element | null => {
        for (const el of [dock, barEl]) {
          if (el && getComputedStyle(el).position !== 'static') return el;
        }
        return barEl;
      })();
      const anchor = anchorEl?.getBoundingClientRect();
      const barPadTop = anchor
        ? anchor.top + (parseFloat(getComputedStyle(anchorEl as Element).borderTopWidth) || 0)
        : dockTop;
      /**
       * QA-BUG-011（宽屏 oval 两块受保护区互撞）：公共牌带默认锚在毛毡 44% 高处，
       * 我的席位卡贴毛毡底部。毛毡在 `.gs__main` 里是 `1fr`，一旦被页脚（加注抽屉展开）
       * 压矮，两者就会相撞 —— 1280×800 单挑实测底池标签压住底牌（可见比 0.8）、
       * 席位卡可见比 0.929。这里给「公共牌带中心线」算一条实测上限：
       *   centerMaxTop = 我的席位卡上沿 - 8 - 毛毡内容盒上沿 - 公共牌带半高
       * 样式层再取 `min(44%, max(78px, var(--center-max-top)))`：毛毡是设计高度时 44%
       * 更小（视觉零变化），被压矮时公共牌带按实测量自动上移，恰好让出 8px 安全间距。
       * 席位卡是底部锚定、公共牌带高度与位置无关，因此不存在测量回环。
       */
      const felt = feltEl?.getBoundingClientRect();
      const feltPad = feltEl ? parseFloat(getComputedStyle(feltEl).borderTopWidth) || 0 : 0;
      const centerMaxTop =
        felt && center ? Math.round(me.top - gap - (felt.top + feltPad) - center.height / 2) : 0;
      const next = {
        lift: Math.max(gap, Math.round(foot.top - eq.bottomY)),
        max: eq.max,
        /* DEF-01：portal 到 overlay 根（= 视口盒）后按视口底换算底边。eq.bottomY 是面板底边的
           视口 Y（席位卡上沿 - gap，或席位卡下方带下界），openBottom 即视口高 - 底边Y。 */
        openBottom: Math.max(gap, Math.round(window.innerHeight - eq.bottomY)),
        // 抽屉的包含块是 `.actionbar`，因此换算基准是它的内容盒上沿，不是按钮行上沿。
        sizerLift: Math.max(gap, Math.round(barPadTop - sizer.bottomY)),
        sizerMax: sizer.max,
        /* DEF-02：portal 到 overlay 根（= 视口盒）后按视口底换算抽屉底边。sizer.bottomY 是抽屉
           底边的视口 Y（席位卡上沿 - gap 或底带下界），sizerOpenBottom 即视口高 - 底边Y。 */
        sizerOpenBottom: Math.max(gap, Math.round(window.innerHeight - sizer.bottomY)),
        centerMaxTop,
        /* R21-M5 · BUG-01：结算 Expanded 浮层落在受保护公共牌带（.felt__center）下沿之下，
           顶边 = centerBottom + gap，可用高度 = 页脚上沿 − 顶边 − gap（不足则浮层内部滚动）。
           覆盖旧的「stack 布局 top:50% 居中」（真机实测每槽压 board ≈300px²，AC-B6 红）。 */
        settleTop: Math.round(centerBottom + gap),
        /* Round25 · M2/C5（round25-settle-body-min）：破根因问题3——旧 `max(160, …)` 把正文夹到 160px
           下限致 5+ 行内滚 + 首行被裁。改 `clamp(minPanelH, 自然可用高, maxPanelH)`：
           minPanelH = header56 + bodyMin136 + footer72 = 264（保正文 ≥6 行，设计 §4.5.2）；
           maxPanelH = min(460, 视口高×0.72)（设计上限）；自然高不足 264 时抬到 264（可轻叠对手网格，
           stack 布局对手区非受保护读数区），绝不再夹到 160 截断正文。 */
        settleMax: Math.min(
          Math.min(460, Math.round(window.innerHeight * 0.72)),
          Math.max(264, Math.round(foot.top - centerBottom - gap * 2)),
        ),
        sizerMode,
        sizerBand: sizerProbe.hardBand,
        /*
         * Round26 · C4（问题2）：ScoreTip float 的安全线。bottomSafeBandTop = `.actionbar` 顶沿（barTop）。
         * float 顶边不压顶栏（max(52, 顶栏下沿 + gap)）；展开详解向下弹，底边 ≤ barTop − gap，
         * 超高内部滚动。float 未渲染（非短屏 / 无评分）时按 52 基线 + 保守可用高，CSS 端仅在
         * `.scoretip--float` 消费，不影响其它形态。
         */
        scoretipTop: (() => {
          const topbarEl = document.querySelector('.topbar');
          const topbarBottom = topbarEl ? topbarEl.getBoundingClientRect().bottom : null;
          return Math.round(solveFloatTop({ topbarBottom, base: 52, gap }));
        })(),
        scoretipDetailMax: (() => {
          const floatEl = document.querySelector('.scoretip--float');
          const topbarEl = document.querySelector('.topbar');
          const topbarBottom = topbarEl ? topbarEl.getBoundingClientRect().bottom : null;
          const floatTop = solveFloatTop({ topbarBottom, base: 52, gap });
          /* 详解向下弹：顶边 = float 容器下沿 + 6；float 未渲染时用 floatTop + 估计 chip 行高 40。 */
          const floatBottom = floatEl ? floatEl.getBoundingClientRect().bottom : floatTop + 40;
          return Math.round(
            solveFloatDetailMaxH({ detailTop: floatBottom + 6, bottomSafeBandTop: barTop, gap, min: 0 }),
          );
        })(),
        heroMirror,
      };
      setEqBox((prev) =>
        prev &&
        Math.abs(prev.lift - next.lift) < 1 &&
        Math.abs(prev.max - next.max) < 1 &&
        Math.abs(prev.openBottom - next.openBottom) < 1 &&
        Math.abs(prev.sizerLift - next.sizerLift) < 1 &&
        Math.abs(prev.sizerMax - next.sizerMax) < 1 &&
        Math.abs(prev.sizerOpenBottom - next.sizerOpenBottom) < 1 &&
        Math.abs(prev.centerMaxTop - next.centerMaxTop) < 1 &&
        Math.abs(prev.settleTop - next.settleTop) < 1 &&
        Math.abs(prev.settleMax - next.settleMax) < 1 &&
        prev.sizerMode === next.sizerMode &&
        Math.abs(prev.sizerBand - next.sizerBand) < 1 &&
        Math.abs(prev.scoretipTop - next.scoretipTop) < 1 &&
        Math.abs(prev.scoretipDetailMax - next.scoretipDetailMax) < 1 &&
        prev.heroMirror === next.heroMirror
          ? prev
          : next,
      );
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  });

  /**
   * FR-A / FR-B：结算/摊牌浮层在场（含淡出期，phase 仍为 handComplete）时铺 scrim。
   * 该标志同时驱动：① overlay 内 scrim 渲染；② `.gs__main` 置 inert（焦点陷阱 + 禁交互）；
   * ③ toast 静默排队（setToastHold）。判据与结算浮层渲染条件完全一致。
   */
  const scrimActive = !!game && game.phase === 'handComplete' && !!c.handSummary && !settleCollapsed;
  /**
   * Round21-M1 · AC-C3：结算浮层是**非阻断型**，Esc / 点遮罩「收起（Peek）」而非推进（收起 ≠ 推进）。
   * 与其它快捷键同一条闸门：任何注册浮层（设置 / 历史 / 二次确认）打开时让位。摊牌/结算浮层未注册，
   * 故这里直接命中。收起后英雄区常清（AC-C4），可再点召回钮恢复展开（内容 diff=0）。
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (overlayOpen()) return;
      if (!game || game.phase !== 'handComplete' || !c.handSummary) return;
      if (settleCollapsed) return;
      e.preventDefault();
      setSettleCollapsed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [game, game?.phase, c.handSummary, settleCollapsed]);
  /** 每手进入 handComplete 时把结算浮层重置为 Expanded（新结算默认完整展示，不继承上一手的收起态）。 */
  useEffect(() => {
    if (game && game.phase === 'handComplete') setSettleCollapsed(false);
  }, [game?.phase, game?.handNumber]);

  /**
   * Round22 · F3：进入淘汰 / 终局的瞬间把对应浮层重置为 Expanded（默认完整展示，不继承上次收起态）。
   * 之后英雄可主动「看牌桌」收起、再「查看详情/结果」召回，收起态不因重渲染回弹。
   */
  useEffect(() => {
    if (c.humanEliminated && !c.observing) setEliminatedCollapsed(false);
  }, [c.humanEliminated, c.observing]);
  useEffect(() => {
    if (game && game.phase === 'gameOver') setGameOverCollapsed(false);
  }, [game?.phase]);

  /**
   * FR-A（无障碍焦点陷阱，PRD 焦点收敛）：scrim 期给牌桌主区 `.gs__main` 置 `inert`
   * —— 既禁鼠标（scrim 已拦截）又禁 Tab 键进入牌桌，同时读屏跳过被压暗的底层，
   * 焦点收敛在 overlay 内的结算浮层。用 DOM 属性方式设置，规避 React 18 的 inert 类型缺口。
   */
  const mainRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    if (scrimActive) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
    } else {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
    }
  }, [scrimActive]);

  /**
   * FR-B（AC-B.3）+ 二期 FR-E3：结算/摊牌浮层在场期间登记「结算态」到让位中枢（state/letway.ts），
   * 由其统一驱动 ① toast 静默排队（复用 setToastHold）② pwa 让位 ③ 禁触发 tooltip/popover，
   * 避免任何 toast 过程态压结算浮层，且结算 CTA/倒计时不被 pwa/tooltip 覆盖（AC-E3.1）。
   */
  useEffect(() => {
    setLetwayFloat('settle', scrimActive);
    return () => setLetwayFloat('settle', false);
  }, [scrimActive]);

  const submit = useCallback(
    (a: Action) => {
      c.humanAct(a);
    },
    [c],
  );

  /*
   * DEF-01：overlay 根节点引用（ref 回调随 `.gs__overlay` 挂载/卸载写入），
   * 供窄屏展开态赢率面板 portal 到平面 C（越过公共牌带 40）。用 ref 回调而非
   * querySelector effect，避免「首帧无牌局→overlay 未渲染→查不到」的时序坑。
   */
  const [eqOverlayRoot, setEqOverlayRoot] = useState<HTMLElement | null>(null);

  if (!game) return null;

  const me = game.players[HUMAN_SEAT];
  const myTurn = game.toAct === HUMAN_SEAT && !c.humanEliminated;
  const busy = c.busyUntil > (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const locked = c.submitLockUntil > (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const legal = myTurn ? game.legalActionsFor(HUMAN_SEAT) : null;
  const active = myTurn && !busy && !locked;
  const disabledReason = c.humanEliminated
    ? '观战中'
    : game.phase === 'handComplete'
      ? '本手已结束'
      : busy
        ? '发牌中'
        : locked
          ? '已提交，稍等'
          : myTurn
            ? ''
            : `等待${game.toAct !== null ? game.players[game.toAct]?.name ?? '对手' : '对手'}行动`;

  const equityDisabledReason = c.humanEliminated ? '观战中不可用：赢率口径依赖你的底牌。' : null;

  /**
   * R14-3：认输入口合并到底部行动栏「弃牌」。顶栏不再有独立入口。
   *   - `concedeArmed` = 越轮认输可用（非我轮时行动栏「弃牌」进入越轮态、点击后二次确认）。
   *   - 条件：处于行动阶段、未被淘汰、我方在牌内、未置认输意图、非发牌动画中。
   *   - 轮到我时由 ActionBar 内 `legal.fold` 直接接管（轮内弃牌无需二次确认），此值不参与。
   * 真正的「轮到我 → 直接 fold / 越轮 → 置 pendingConcede」判定全部收在 controller.concedeAnytime。
   */
  const inBettingPhase =
    game.phase === 'preflop' || game.phase === 'flop' || game.phase === 'turn' || game.phase === 'river';
  const alreadyOut = !me || me.folded || c.pendingConcede;
  const concedeArmed = inBettingPhase && !alreadyOut && !busy && !c.humanEliminated;
  /**
   * 不再按固定时长淡出：可见性 = `lastScore` 存在（controller 在提交下一动作 / 进入下一手需重新
   * 行动时清空它）且未被玩家显式关闭（✕ / Esc → dismissedScoreAt）。
   * 展示内容（三段式解析 + k/n 翻页）由 ScoreTip 从「本手评分记录」现算，这里只筛出本手记录。
   */
  const scoreAt = c.lastScore?.at ?? null;
  const scoreOn = scoreTipVisible(scoreAt, dismissedScoreAt);
  const handScores = scoreOn && c.lastScore ? c.scores.filter((s) => s.handNumber === c.lastScore!.handNumber) : [];
  const scoreShown = scoreOn && handScores.length > 0;
  const dismissScore = (): void => setDismissedScoreAt(scoreAt);
  // T8 · AC-N1：净盈利 > 0 的手，向评分区透传本手结果，让首行先陈述「你赢下 +78 · 两对」。
  const handResult =
    game.phase === 'handComplete' && c.handSummary?.won
      ? { net: c.handSummary.net, handText: c.handSummary.hero?.handText ?? c.handSummary.focus?.handText ?? null }
      : null;
  const nextIn = c.nextHandAt ? Math.max(0, Math.ceil((c.nextHandAt - Date.now()) / 1000)) : null;

  const clockTotalMs = settings.actionClock * 1000;

  /**
   * 赢率面板四档形态（MAJ-1）：一套状态机、四种承载方式。
   *   xl → 右侧常驻栏；lg → 右下角可折叠浮层；md → 横向赢率条；sm → 底部抽屉。
   * lg/md/sm 三档都挂在页脚里，用 CSS 锚定在行动区上沿之上，天然不遮挡行动热区。
   */
  const eqForm = bp === 'xl' ? 'aside' : bp === 'lg' ? 'float' : bp === 'md' ? 'bar' : 'drawer';
  const narrowForm = eqForm === 'bar' || eqForm === 'drawer';
  /*
   * 折叠态口径：
   *   xl   常驻侧栏，不折叠；
   *   lg   浮层，默认展开（224×208），折叠为 40 高标题条，记忆在 equityCollapsed；
   *   md/sm 默认折叠（横条 56 / 把手 44），记忆在 equityCollapsedNarrow；
   * 另外加注抽屉展开时 md/sm 强制收成单行，避免两个浮层同屏抢占牌桌与行动区。
   */
  const eqUserCollapsed = narrowForm
    ? settings.equityCollapsedNarrow
    : eqForm === 'float'
      ? settings.equityCollapsed
      : false;
  /*
   * FR2（设计方案 5.2 核心杠杆）：弃牌后推进态（c.foldAdvancePrompt）默认折叠分析面板，
   * 把下半屏「分析面板 + 失误提示条 + 操作条」三块从「整块面板」降为「一行把手」，
   * 让操作条固定可见、不被挤出视口，消除同屏拥挤（AC2.2 / AC2.3）。
   * 仅对可折叠形态生效：窄屏 bar/drawer 与 lg float；xl 常驻侧栏无折叠语义（折叠会把
   * 桌面侧栏压成单行、属回归），保持不变。用户仍可在推进态手动展开（展开态内部滚动）。
   */
  const eqCollapsed = narrowForm
    ? eqUserCollapsed || sizerOpen || c.foldAdvancePrompt
    : eqForm === 'float'
      ? eqUserCollapsed || c.foldAdvancePrompt
      : eqUserCollapsed;
  /*
   * QA-BUG-R5-DRIFT（R5 防漂移）：窄屏(drawer=sm/xs，含 430×932 目标)休息态若为「展开」，
   * 赢率浮层是绝对定位（0 文档流高）；打开 sizer 时若强制折叠成**文档流内**的 44px 把手
   * → 页脚 +52 → 压矮 .gs__main/.felt → 竖屏对手区收缩、席位漂移（189×124 → 125×52）。
   * 这里当「drawer 休息态展开 + 开 sizer」时把赢率整块隐藏（display:none，仍是 0 文档流高），
   * 页脚高度守恒；休息态本就折叠（把手在流里）的情形保持不变。md(bar) 抽屉仍在文档流内、
   * 不属两目标口径，维持改前「强制折叠」行为，不引入新变化。
   */
  const eqHiddenForSizer = eqForm === 'drawer' && sizerOpen && !eqUserCollapsed;

  /*
   * DEF-01（Round2 阻断修复 · 层级）：窄屏展开态 portal 到 overlay 根 `.gs__overlay`。
   * `.gs__foot` 是 isolate + z:20 的层叠上下文，其内的赢率壳被封顶在 20、压不过公共牌带
   * `--z-board:40`；面板长高覆盖公共牌带后，公共牌/底池会从上方打穿、主数字/三行不可点。
   * 沿用 Round2 问题2 风格卡的既定做法：展开态（且未被 sizer 隐藏）portal 到 overlay 根、
   * 走有效层级 --z-eqfloat:110（> board 40）。折叠把手仍留在页脚文档流。
   */
  const portalOpenPanel = narrowForm && !eqCollapsed && !eqHiddenForSizer;

  // 注意：此处不能用 useMemo —— 上方存在提前 return（无牌局 `if (!game) return null`），
  // 在提前 return 之后调用 Hook 会破坏调用顺序。面板渲染本身很轻，直接构造即可。
  const equityShell = (
    <div
      className={`eqshell eqshell--${eqForm} ${eqCollapsed ? 'eqshell--collapsed' : 'eqshell--open'}${eqHiddenForSizer ? ' eqshell--sizerhidden' : ''}${portalOpenPanel ? ' eqshell--portal' : ''}`}
      data-form={eqForm}
      data-acting={active ? '1' : undefined}
    >
      <EquityPanel
        state={eq}
        enabled={settings.equityEnabled}
        collapsed={eqCollapsed}
        seatCount={game.config.seatCount}
        onToggleEnabled={(on) => c.setEquityEnabled(on)}
        onToggleCollapse={() =>
          patchSettings(
            narrowForm
              ? { equityCollapsedNarrow: !settings.equityCollapsedNarrow }
              : { equityCollapsed: !settings.equityCollapsed },
          )
        }
        onModeChange={(m) => c.setEquityMode(m)}
        onRetry={() => c.equity.retry()}
        disabledReason={equityDisabledReason}
      />
    </div>
  );

  return (
    <div
      className={`gs gs--${bp}${shortVp ? ' gs--short' : ''}`}
      data-acting={active ? '1' : undefined}
      /* BLK-F1：抽屉承载模式与选档带宽都落到 DOM 上 —— 样式层据此换排版，量测脚本据此取证 */
      data-sizer-mode={eqBox ? eqBox.sizerMode : undefined}
      data-sizer-band={eqBox ? String(eqBox.sizerBand) : undefined}
      style={
        {
          ...(footH ? { '--foot-h': `${Math.round(footH)}px` } : {}),
          // 只有窄断点（bar / drawer 两种浮层形态）需要为「我的席位卡」抬高展开态浮层
          ...(eqBox && eqBox.centerMaxTop > 0 ? { '--center-max-top': `${eqBox.centerMaxTop}px` } : {}),
          /* R21-M5 · BUG-01：stack 结算 Expanded 浮层顶边/限高（避开公共牌带），CSS 仅在
             .gs--sm/.gs--short .settle:not(--peek) 消费；未测时回退 50%/60%。 */
          ...(eqBox && eqBox.settleTop > 0
            ? { '--settle-top': `${eqBox.settleTop}px`, '--settle-max': `${eqBox.settleMax}px` }
            : {}),
          /* QA-BUG-R5-DRIFT：加注抽屉现在**所有断点**都是浮层，因此 --sizer-lift / --sizer-open-max
             需无条件下发（改前仅窄屏下发，宽屏抽屉退回默认 bottom/limit、会盖住我的席位卡）。 */
          ...(eqBox
            ? {
                '--sizer-lift': `${eqBox.sizerLift}px`,
                '--sizer-open-max': `${eqBox.sizerMax}px`,
                /* DEF-02：portal 到 overlay 根后按视口底定位展开态抽屉底边 */
                '--sizer-open-bottom': `${eqBox.sizerOpenBottom}px`,
              }
            : {}),
          ...(narrowForm && eqBox
            ? {
                '--eq-lift': `${eqBox.lift}px`,
                '--eq-open-max': `${eqBox.max}px`,
                /* DEF-01：portal 到 overlay 根后按视口底定位展开态面板底边 */
                '--eq-open-bottom': `${eqBox.openBottom}px`,
              }
            : {}),
          /* Round26 · C4：ScoreTip float 顶边（不压顶栏）+ 展开详解限高（底边不越操作带顶沿）。
             CSS 仅在 `.scoretip--float` 段消费；未测时 CSS 回退固定 52px + 无限高。 */
          ...(eqBox
            ? {
                '--scoretip-float-top': `${eqBox.scoretipTop}px`,
                '--scoretip-detail-max': `${eqBox.scoretipDetailMax}px`,
              }
            : {}),
        } as CSSProperties
      }
    >
      <header className="topbar">
        {/* QA-BUG-027：牌桌页改前没有 h1，读屏用户拿不到页面主标题。视觉信息已由顶栏胶囊表达，
            这里补一条仅读屏可见的标题，不改动顶栏视觉。
            PM 终验收红线（脱敏）：这条 sr-only 标题会被读屏朗读、也会被页面文本抓取工具取到，
            同样属于对外暴露。因此**改写措辞**而不是删除元素 —— 删掉会连带破坏
            landmark / h1 结构（设计验收已闭环项）。语义保持「这是牌桌主界面」不变。 */}
        <h1 className="sr-only">Project Riverside 单机人机牌局 · 牌桌</h1>
        <div className="topbar__left">
          <span className="topbar__hand">
            第 <b className="num">{game.handNumber}</b> 手
          </span>
          <span className="topbar__chip">{STREET_CN[game.street]}</span>
          <span className="topbar__chip num">
            盲注 {game.config.smallBlind}/{game.config.bigBlind}
          </span>
          <span className="topbar__chip num">底池 {chips(game.pot)}</span>
          <span className="topbar__chip num">你的筹码 {chips(me?.stack ?? 0)}</span>
        </div>
        <div className="topbar__right">
          {c.saveError ? <span className="topbar__warn">进度未保存</span> : null}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => {
              track('replay_open', { source: 'topbar', hands: c.handHistory().length });
              setHistoryOpen(true);
            }}
          >
            手牌历史 <kbd>H</kbd>
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setSettingsOpen(true)}>
            设置 <kbd>S</kbd>
          </button>
        </div>
      </header>

      <main className="gs__main" ref={mainRef}>
        <div className="gs__table">
          <Table
            c={c}
            game={game}
            compactSeats={bp === 'md' || stackLayout}
            layout={stackLayout ? 'stack' : 'oval'}
            /* Round23 FR-4：单机手结束（handComplete）/ 终局（gameOver）全员亮牌复盘，
               弃牌者也亮底牌 + 现算牌型 + 「已弃牌」区分（联机 MpTable 不走本组件，零改动）。 */
            revealAll={game.phase === 'handComplete' || game.phase === 'gameOver'}
            clockTotalMs={clockTotalMs}
            winningCards={game.phase === 'handComplete' ? c.handSummary?.winningCards ?? null : null}
          />
        </div>

        {eqForm === 'aside' ? <aside className="gs__side">{equityShell}</aside> : null}
      </main>

      {/* R2.3 的短屏浮动评分角标（ScoreTip variant="float"）已随 v2 迁入下方 overlay 根 `.gs__overlay`
          （平面 C，z:120），与结算/toast 同处唯一可比较的层叠上下文。 */}

      <footer className="gs__foot" ref={setFootEl}>
        {eqForm === 'aside' || portalOpenPanel ? null : equityShell}
        {/* QA-BUG-004：评分提示放在固定高度的常驻槽里 —— 提示出现 / 消失都不改变页脚高度，
            锚在页脚上沿的赢率浮层位移恒为 0，不再有周期性的跳动与遮挡增长。
            R2：档位徽标常驻 + EV 详情 hover/pin + 复盘入口，见 ScoreTip。
            Round26 · C4（§4.2.3）：sizer 打开时 inline 评价条让位（收起）——sizer 浮层上抬会覆到
            页脚提示槽 / 其向上弹的详解与 sizer 面板叠压。评价是事后反馈，可在「手牌历史 → 决策评分」
            回看；关面板即恢复。短屏 float 角标在顶栏下方安全区、与 sizer 不相交，故不随之隐藏。 */}
        <div className="gs__foot-tip">
          {scoreShown && !sizerOpen ? <ScoreTip records={handScores} variant="inline" tone={settings.scoreToneMode} handResult={handResult} onReplay={openScoreReplay} onClose={dismissScore} /> : null}
        </div>
        {c.lastActionError ? (
          <p className="actionerr" role="alert">
            {c.lastActionError}
          </p>
        ) : null}
        <ActionBar
          legal={legal}
          active={active}
          disabledReason={disabledReason}
          pot={game.pot}
          myStack={me?.stack ?? 0}
          myStreetContributed={me?.streetContributed ?? 0}
          bigBlind={game.config.bigBlind}
          settings={settings}
          clockRemainingMs={c.clockRemainingMs}
          /* BLK-F1：短屏（视口高 ≤640 或实测带宽装不下滑杆）改用方案 B 的单行快捷档承载 */
          sizerMode={eqBox && narrowForm ? eqBox.sizerMode : 'full'}
          /* Round26 · C3（问题1 · 方案 A）：竖屏窄带无纯几何解时，面板顶部镜像 Hero 两张底牌，
             保证「下注全程看得见自己的牌」（B3 visFrac=1）。仅窄屏浮层化路径启用。 */
          heroMirror={!!(eqBox && narrowForm && eqBox.heroMirror)}
          heroHole={me?.hole ?? null}
          onSizerOpenChange={setSizerOpen}
          /* DEF-02（Round2 阻断修复 · 层级）：窄屏加注抽屉浮层与赢率面板同因——`.gs__foot`
             isolate + z:20 把抽屉封顶在 20，360×640 上抽屉长高浮到英雄区后被公共牌/我的席位/
             毛毡/气泡从上方打穿，CTA elementFromPoint 命中 0/5。把抽屉 portal 到 overlay 根
             （平面 C，有效 z --z-eqfloat:110 > board 40），几何位置不变、只提层，CTA 五点命中自身。 */
          sizerPortalRoot={narrowForm ? eqOverlayRoot : null}
          onSubmit={submit}
          /* R14-3：底部「弃牌」是唯一认输入口。轮内由 legal.fold 直接 fold；
             非我轮时 concedeArmed 让它进入越轮态（点击弹二次确认 → onConcede）。 */
          concedeArmed={concedeArmed}
          onConcede={() => c.concedeAnytime()}
          /* Round21 T6 / AC-S1：越轮弃牌预选可取消——预选态由 pendingConcede 驱动，
             行动栏据此把「弃牌」切到「取消预选」，点击回滚意图（不触引擎、无 fold 落地）。 */
          concedePending={c.pendingConcede}
          onCancelConcede={() => c.cancelConcede()}
          foldPrompt={c.foldAdvancePrompt}
          onFoldAdvance={(mode, remember) => c.chooseFoldAdvance(mode, remember)}
        />
      </footer>

      {/* ============================ 平面 C · Overlay 根（FR-A 层级契约核心） ============================
          header/main/footer 的兄弟节点。isolation:isolate + z:80 把 scrim/toast/失误浮标/结算等
          全部封进同一个可比较的层叠上下文，根除 v1 跨上下文 z 值幻象（AC-A.1）。
          子浮层各自的层级由 CSS token 决定（scrim100 < toast115 < 失误条120 < 结算140）。 */}
      <div className="gs__overlay" ref={setEqOverlayRoot}>
        {/* DEF-01：窄屏展开态赢率面板 portal 到此（平面 C，有效 z --z-eqfloat:110 > 公共牌带 40）。
            折叠态仍在页脚文档流；展开且 overlay 根已挂载时才走 portal，否则回退页脚内联渲染。 */}
        {portalOpenPanel && eqOverlayRoot ? createPortal(equityShell, eqOverlayRoot) : null}
        {/* scrim（AC-A.2/A.3）：结算/摊牌浮层在场时铺满，压暗牌桌 + 拦截落到牌桌的点击（底层禁交互）。
            Round21-M1 · AC-C3：点遮罩「收起（Peek）」而非推进——收起 ≠ 推进（防误触跳手），
            收起后英雄区常清、可召回；真正推进仍只走结算 CTA。 */}
        {scrimActive ? (
          <div
            className={`overlay__scrim${settleClosing ? ' overlay__scrim--closing' : ''}`}
            aria-hidden="true"
            onClick={() => setSettleCollapsed(true)}
          />
        ) : null}

        {/* 短屏浮动评分角标（承接 v1 R2.3）：迁入 overlay 根后走 --z-scoretip(120)，仍由 CSS 媒体查询显示。 */}
        {scoreShown ? (
          <ScoreTip records={handScores} variant="float" tone={settings.scoreToneMode} handResult={handResult} onReplay={openScoreReplay} onClose={dismissScore} />
        ) : null}

        {/* 普通 toast（AC-B.2）：从 App 根迁入，z:115（低于结算 140、失误条 120）；结算/摊牌期由
            setToastHold 静默排队，不在此渲染过程态。顶部安全条定位见 base.css .toast-layer。 */}
        <ToastLayer items={toasts} />

        {/* 结算浮层（AC-A.2）：实底 #0d1a14、z:140，随 scrim 一起出现/淡出。
            Round21-M1 · T3 三态：Expanded（默认）↔ Peek（收起瘦条 + 召回钮）。收起时撤 scrim/inert，
            浮层内容保持挂载（恢复后 diff=0）；关闭键脱离 flex（.overlay-close absolute 48×48）。 */}
        {game.phase === 'handComplete' && c.handSummary ? (
          <div
            className={`settle${settleClosing ? ' settle--closing' : ''}${settleCollapsed ? ' settle--peek' : ''}`}
            role="status"
            aria-live="polite"
          >
            {settleCollapsed ? (
              // Peek 态：只留一行摘要 + 召回钮（AC-C2：可一键恢复展开，内容 diff=0）。
              <button
                type="button"
                className="settle__recall"
                onClick={() => setSettleCollapsed(false)}
                aria-label="展开结算详情"
              >
                <span
                  className={`settle__net num${c.handSummary.net > 0 ? ' pos' : c.handSummary.net < 0 ? ' neg' : ''}`}
                >
                  {c.handSummary.text}
                </span>
                <span className="settle__recall-hint">展开结算</span>
              </button>
            ) : (
              <>
                {/* AC-C1：关闭（收起）键脱离 flex 竞争，absolute 右上角、命中 48×48、在视口内、命中自身。 */}
                <button
                  type="button"
                  className="overlay-close"
                  aria-label="收起结算，查看牌桌"
                  onClick={() => setSettleCollapsed(true)}
                >
                  ✕
                </button>
                {/* Round23 BUG-R02：结算浮层三段骨架——header/scroll-body(可滚) + sticky footer(主 CTA 常驻)。
                    header(结果焦点行) + body(可变明细，仅此区滚动) + footer(倒计时 + 主 CTA，flex:none + sticky bottom)。 */}
                <div className="settle__body">
                  {/* R5：赢家单一焦点 —— 胜者 + 主池净额 + 制胜牌型（摊牌手才有牌型），一眼读懂「谁赢/赢多少/靠什么」 */}
                  {c.handSummary.focus ? (
                    <div className="settle__focus">
                      <span className="settle__focus-win">{c.handSummary.focus.winners} 赢下</span>
                      <span className="settle__focus-amt num">{c.handSummary.focus.amount}</span>
                      {c.handSummary.focus.handText ? (
                        <span className="settle__focus-hand">{c.handSummary.focus.handText}</span>
                      ) : null}
                    </div>
                  ) : null}
                  {/* T8 · AC-N2：结算首屏结构化一行「你的牌 A♠K♦ · 两对 · +78」——底牌 + 成手牌型名 + 盈亏，
                      首屏不出现 EV/GTO/VPIP 术语；弃牌手无牌型则标「已弃牌」，盈亏照实。 */}
                  {c.handSummary.hero?.holeText ? (
                    <div className="settle__hero">
                      <span className="settle__hero-label">你的牌</span>
                      <span className="settle__hero-hole num">{c.handSummary.hero.holeText}</span>
                      <span className="settle__hero-hand">
                        {c.handSummary.hero.handText ?? (c.handSummary.hero.folded ? '已弃牌' : '未成手')}
                      </span>
                      <span
                        className={`settle__hero-net num${c.handSummary.net > 0 ? ' pos' : c.handSummary.net < 0 ? ' neg' : ''}`}
                      >
                        {signedChips(c.handSummary.net)}
                      </span>
                    </div>
                  ) : null}
                  {/* Round26 · A类 Bug1：净盈亏**单一摘要**——避免与上方 .settle__hero-net 冗余叠压。
                      Hero 结构化行在场时净盈亏已并入「你的牌 A♠K♦ · 两对 · +78」（.settle__hero-net），
                      此处不再重复渲染；仅当无 Hero 行（未发牌/观战）时用 .settle__head 兜底展示净盈亏。
                      牌型名 / 金额 badge / 主池边池明细各自在 .settle__body 独立流式排布，正文超高走内部
                      滚动（.settle__body{overflow-y:auto}）而非物理叠压。 */}
                  {!c.handSummary.hero?.holeText ? (
                    <div className="settle__head">
                      <span
                        className={`settle__net num${c.handSummary.net > 0 ? ' pos' : c.handSummary.net < 0 ? ' neg' : ''}`}
                      >
                        {c.handSummary.text}
                      </span>
                    </div>
                  ) : null}
                  {/* 主/边池归属明细：主池已在焦点行聚合，这里补边池 / 零头等完整口径 */}
                  {c.handSummary.awards.length > 1 ? (
                    <ul className="settle__awards">
                      {c.handSummary.awards.map((a, i) => (
                        <li key={i}>{a}</li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {/* sticky footer：倒计时行 + 主 CTA。flex:none + position:sticky bottom:0 → 内容再多也恒可见（visFrac=1）。 */}
                <div className="settle__footer">
                  {/* FR1（设计方案 4.2 主方案）：倒计时/提示作为独立一行置于 CTA 上方，
                      倒计时数字用 .settle__count 局部强调、绝不进入按钮盒 —— 与「继续 / 下一手」
                      主文案结构分离、视觉分层、零重叠（AC1.2）。摊牌手无倒计时（AC1.4）。 */}
                  <div className="settle__hint">
                    {c.handSummary.isShowdown ? (
                      '本手结束 · 看清后点「下一手」'
                    ) : nextIn !== null ? (
                      <>
                        <span className="settle__count num">{nextIn}</span>s 后自动继续 · 可提前
                      </>
                    ) : (
                      '本手结束 · 点「下一手」继续'
                    )}
                  </div>
                  {/* AC-C1/C3：一行两键——「查看牌桌」收起（非阻断收起路径），「继续 / 下一手」推进（唯一推进出口）。 */}
                  <div className="settle__actions">
                    <button type="button" className="btn" onClick={() => setSettleCollapsed(true)}>
                      查看牌桌
                    </button>
                    <button type="button" className="btn btn--primary" onClick={beginAdvance}>
                      继续 / 下一手
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        ) : null}

        {/* Round22 · F3：终局大屏改为 overlay 根内的浮层，Table 保持挂载（不再卸载，AC3.4）。
            Expanded 时 .over--overlay 铺满牌桌区、scrim 压暗底层；Collapsed 时收成顶部瘦条、撤 scrim、
            露出真实牌桌复盘各家底牌。收起 ≠ 退出，瘦条整条可点召回。 */}
        {game.phase === 'gameOver' ? (
          <GameOverScreen
            game={game}
            collapsed={gameOverCollapsed}
            handsPlayed={game.handNumber}
            peakStack={c.peakStack}
            humanSeat={HUMAN_SEAT}
            records={c.handHistory()}
            onHome={onHome}
            onRestart={() => lastSetup && onRestart(lastSetup)}
            onReview={() => {
              track('replay_open', { source: 'game_over', hands: c.handHistory().length });
              setHistoryOpen(true);
            }}
            onCollapse={() => setGameOverCollapsed(true)}
            onExpand={() => setGameOverCollapsed(false)}
          />
        ) : null}
      </div>

      <Drawer
        open={historyOpen}
        title="手牌历史与复盘"
        onClose={() => setHistoryOpen(false)}
        side={bp === 'sm' ? 'bottom' : 'right'}
        size={bp === 'sm' ? 520 : 560}
      >
        <HistoryDrawer
          records={c.handHistory()}
          snapshots={c.snapshots}
          scores={c.scores}
          humanSeat={HUMAN_SEAT}
          names={names}
          revealFolded={settings.revealFolded}
          onRevealFoldedChange={(v) => patchSettings({ revealFolded: v })}
          tone={settings.scoreToneMode}
        />
      </Drawer>

      <Drawer open={settingsOpen} title="设置" onClose={() => setSettingsOpen(false)} side={bp === 'sm' ? 'bottom' : 'right'} size={bp === 'sm' ? 560 : 440}>
        <SettingsPanel
          settings={settings}
          game={game}
          styles={c.styles}
          equityDisabledReason={equityDisabledReason}
          onPatch={(p) => patchSettings(p)}
          onSpeed={(s) => c.setSpeed(s)}
          onEquityEnabled={(on) => c.setEquityEnabled(on)}
          onExitSave={() => {
            setSettingsOpen(false);
            c.exitAndSave();
            onHome();
          }}
          onQuitToHome={() => {
            setSettingsOpen(false);
            c.abandonSave();
            c.reset();
            onHome();
          }}
        />
      </Drawer>

      <EliminatedDialog
        open={c.humanEliminated && !c.observing}
        collapsed={eliminatedCollapsed}
        handNumber={game.handNumber}
        peakStack={c.peakStack}
        onObserve={() => c.observeToEnd()}
        onRestart={() => lastSetup && onRestart(lastSetup)}
        onHome={() => {
          c.reset();
          onHome();
        }}
        onCollapse={() => setEliminatedCollapsed(true)}
        onExpand={() => setEliminatedCollapsed(false)}
      />
    </div>
  );
}
